/**
 * Entities — the legal and economic subjects documents describe and projects analyse.
 * See docs/adr/0003-entities.md.
 *
 * Named `LegalEntity` in code because "entity" already means "the document or project a prompt
 * belongs to" elsewhere in the client (the prompts dialog's `entityType`, for one). The UI says
 * "Entities".
 */

export type LegalEntity = {
  id: string;
  /**
   * Permanent short code (`E7`) — what prompts refer to this entity by, assigned once at creation and
   * never changed (ADR 0003 decision 12). Absent on entities created before codes existed; those
   * cannot be used by the pipeline.
   */
  code?: string;
  name: string;
  /** Code from the ENTT lookup — what the entity IS (Company, Fund, SPV …). */
  entityType: string;
  legalName: string;
  description: string;
  /**
   * Other names the entity appears under in documents. Matters more than it looks: documents rarely
   * use the legal name, and the pipeline matches entities to text.
   */
  aliases: string[];
  itemStatus: number;
  updatedAt?: number;
};

export type LegalEntityPayload = {
  name: string;
  entity_type: string;
  legal_name: string;
  description: string;
  aliases: string[];
};

/**
 * One entity's association with a document or project. `role` (ENTR lookup) belongs HERE, not on
 * the entity: the same company can be the Target in one deal and the Acquirer in another.
 */
export type EntityAssociation = {
  entityId: string;
  role: string | null;
};

/** What a document or project carries. Exactly one primary; with a single entity it is automatic. */
export type EntityAssociationSet = {
  entities: EntityAssociation[];
  primaryEntityId: string | null;
};

export const ARCHIVED = 9;

/** Anything that names an entity — a project association (which has a role) or a document run link. */
export type EntityRef = { entityId: string };

/** Do two sets share at least one entity? Mirrors the server rule exactly. */
export function sharesAnEntity(a?: EntityRef[] | null, b?: EntityRef[] | null): boolean {
  const ids = new Set((a ?? []).map((e) => e.entityId));
  return (b ?? []).some((e) => ids.has(e.entityId));
}

/**
 * True when a record's entity associations changed after its latest run — its results were produced
 * against a different set of entities and need rerunning (ADR 0003, decision 4).
 */
export function entitiesChangedSinceRun(entitiesUpdatedAt?: number, latestRunTimestamp?: number | string): boolean {
  const latest = Number(latestRunTimestamp) || 0;
  return !!entitiesUpdatedAt && latest > 0 && entitiesUpdatedAt > latest;
}

/**
 * A document run's entity links (ADR 0003 decisions 6-10). Links belong to a RUN: the model makes them
 * from the organisation's entity list, users correct them per run, and projects judge a document by the
 * run they pin.
 */
/**
 * A document run's link to one entity. **No role**: a document is simply *about* an entity, whereas
 * target/acquirer describes a deal, which is a project (ADR 0003, revised 2026-09-24).
 */
export type RunEntityLink = {
  entityId: string;
  source: 'model' | 'user';
  /** Whether the model gave a code (E2) or a name — a prompt drifting away from codes is visible. */
  resolved_by?: 'handle' | 'name';
  /** Set when the link came from an artefact attribution rather than the document-level answer. */
  via?: 'artefact';
  /** Who set it, for user-made links — the person who made THIS link, not whoever last saved the set. */
  set_by_id?: string;
  set_by_name?: string;
  set_at?: number;
};

export type RunEntityLinks = {
  entities: RunEntityLink[];
  primaryEntityId: string | null;
  primary_source?: 'model' | 'user' | null;
  unlisted_entities?: string[];
  /** Reported as unlisted but resolvable to an entity that already exists, under another of its names. */
  known_under_another_name?: { reported: string; entityId: string; name: string; linked: boolean }[];
  rejected?: string[];
  /** Entity-looking fields the model returned under names the pipeline does not know. */
  unrecognised_entity_fields?: string[];
  /** The amber condition: no primary, unlisted entities, unrecognised fields, or no links at all. */
  needs_attention?: boolean;
  resolved_at?: number;
  corrected_at?: number;
};

type RunsLike = { runs?: Record<string, { state?: string; entity_links?: RunEntityLinks | null } | undefined> } | undefined;

/** The links of one run, or null. */
export function runEntityLinks(pipelineState: unknown, runId?: string | number | null): RunEntityLinks | null {
  if (runId == null) return null;
  return (pipelineState as RunsLike)?.runs?.[String(runId)]?.entity_links ?? null;
}

/**
 * The latest COMPLETE run's links — what a document "has" for listing and filtering. Derived rather
 * than copied onto the document, so it cannot drift from the run it describes.
 */
export function latestCompleteRunLinks(pipelineState: unknown): { runId: string; links: RunEntityLinks } | null {
  const runs = (pipelineState as RunsLike)?.runs ?? {};
  const ids = Object.keys(runs)
    .filter((id) => runs[id]?.state === 'complete' && runs[id]?.entity_links)
    .sort((a, b) => Number(b) - Number(a));
  return ids.length ? { runId: ids[0], links: runs[ids[0]]!.entity_links as RunEntityLinks } : null;
}

/**
 * Does a document run need someone's attention (ADR 0003 decision 10)?
 *
 * Three cases, all of which mean the same thing to a user — this run is finished but its entities are
 * not settled:
 *   - the run resolved links but found no primary;
 *   - it found entities that are not in the organisation's list;
 *   - it linked nothing at all. **Until a run links at least one entity, its document cannot be added
 *     to any project**, so a plain green "complete" would overstate what the run achieved. This case
 *     covers runs made before artefaction linked entities, and runs whose prompt does not yet ask for
 *     links.
 *
 * Only for FINISHED runs: a run still working has not failed to do anything yet.
 */
export function runNeedsAttention(run?: { state?: string; entity_links?: RunEntityLinks | null } | null): boolean {
  if (run?.state !== 'complete') return false;
  const links = run.entity_links;
  if (!links) return true;
  return Boolean(links.needs_attention) || !(links.entities?.length);
}

